import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Badge,
  Collapse,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  PlayArrow as RunningIcon,
  Block as CancelledIcon,
  Visibility as ViewIcon,
  Pause as PauseIcon,
  PlayCircleOutline as ResumeIcon,
} from '@mui/icons-material';
import FlexService from '../../services/FlexService';

/**
 * FlexTaskMonitor - Displays running and recent FLEX script tasks
 *
 * Features:
 * - Real-time task list with progress
 * - Cancel running tasks
 * - View task details and errors
 * - Auto-refresh while tasks are running
 */
function FlexTaskMonitor({ isCollapsed = false, onToggleCollapse, standalone = false }) {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true); // Auto-refresh toggle

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const [taskList, taskStats] = await Promise.all([
        FlexService.listTasks(),
        FlexService.getTaskStats(),
      ]);
      setTasks(taskList);
      setStats(taskStats);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Auto-refresh interval (only when enabled and there are active tasks)
  useEffect(() => {
    if (!autoRefresh) return;

    const hasActiveTasks = tasks.some(t => t.status === 'RUNNING' || t.status === 'PENDING');
    if (!hasActiveTasks) return;

    const interval = setInterval(() => {
      fetchTasks();
    }, 3000);

    return () => clearInterval(interval);
  }, [autoRefresh, tasks, fetchTasks]);

  // Cancel task
  const handleCancelClick = (task) => {
    setSelectedTask(task);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedTask) return;

    try {
      await FlexService.cancelTask(selectedTask.taskId, cancelReason || 'Cancelled by user');
      setCancelDialogOpen(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      console.error('Failed to cancel task:', error);
    }
  };

  // View details
  const handleViewDetails = (task) => {
    setSelectedTask(task);
    setDetailsDialogOpen(true);
  };

  // Status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <SuccessIcon sx={{ color: 'success.main', fontSize: 18 }} />;
      case 'FAILED':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 18 }} />;
      case 'CANCELLED':
        return <CancelledIcon sx={{ color: 'warning.main', fontSize: 18 }} />;
      case 'RUNNING':
        return <RunningIcon sx={{ color: 'info.main', fontSize: 18 }} />;
      case 'PENDING':
        return <PendingIcon sx={{ color: 'text.secondary', fontSize: 18 }} />;
      default:
        return null;
    }
  };

  // Status chip
  const getStatusChip = (status) => {
    const colors = {
      COMPLETED: 'success',
      FAILED: 'error',
      CANCELLED: 'warning',
      RUNNING: 'info',
      PENDING: 'default',
    };
    return (
      <Chip
        icon={getStatusIcon(status)}
        label={status}
        size="small"
        color={colors[status] || 'default'}
        sx={{ height: 24, fontSize: 11 }}
      />
    );
  };

  const runningCount = tasks.filter(t => t.status === 'RUNNING' || t.status === 'PENDING').length;

  // Standalone mode - render content directly without collapsible header
  if (standalone) {
    return (
      <Box sx={{ bgcolor: 'background.paper', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Badge badgeContent={runningCount} color="info" max={99}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Active Tasks
            </Typography>
          </Badge>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={autoRefresh ? 'Pause auto-refresh' : 'Resume auto-refresh'}>
            <IconButton
              onClick={() => setAutoRefresh(!autoRefresh)}
              color={autoRefresh ? 'primary' : 'default'}
              size="small"
            >
              {autoRefresh ? <PauseIcon /> : <ResumeIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh now">
            <IconButton onClick={fetchTasks} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {!autoRefresh && (
            <Chip label="Auto-refresh paused" size="small" color="warning" sx={{ height: 24, fontSize: 11 }} />
          )}
          {stats && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {stats.running > 0 && (
                <Chip label={`${stats.running} running`} size="small" color="info" sx={{ height: 24, fontSize: 11 }} />
              )}
              {stats.pending > 0 && (
                <Chip label={`${stats.pending} pending`} size="small" color="default" sx={{ height: 24, fontSize: 11 }} />
              )}
              {stats.completed > 0 && (
                <Chip label={`${stats.completed} completed`} size="small" color="success" sx={{ height: 24, fontSize: 11 }} />
              )}
              {stats.failed > 0 && (
                <Chip label={`${stats.failed} failed`} size="small" color="error" sx={{ height: 24, fontSize: 11 }} />
              )}
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {tasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No tasks found
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'background.default' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Script</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Progress</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.map((task) => (
                    <TableRow key={task.taskId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {task.scriptName}
                        </Typography>
                        {task.progressMessage && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {task.progressMessage}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusChip(task.status)}
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        {task.status === 'RUNNING' && task.progressTotal > 0 ? (
                          <Box>
                            <LinearProgress
                              variant="determinate"
                              value={task.progressPercent || 0}
                              sx={{ height: 6, borderRadius: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {task.progressCurrent}/{task.progressTotal}
                            </Typography>
                          </Box>
                        ) : task.status === 'RUNNING' ? (
                          <LinearProgress sx={{ height: 6, borderRadius: 1 }} />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                          {task.duration || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewDetails(task)}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(task.status === 'RUNNING' || task.status === 'PENDING') && (
                            <Tooltip title="Cancel">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleCancelClick(task)}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Cancel Dialog */}
        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Cancel Task</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Are you sure you want to cancel task "{selectedTask?.scriptName}"?
            </Typography>
            <TextField
              label="Reason (optional)"
              fullWidth
              size="small"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g., Taking too long"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)}>Keep Running</Button>
            <Button onClick={handleCancelConfirm} color="error" variant="contained">
              Cancel Task
            </Button>
          </DialogActions>
        </Dialog>

        {/* Details Dialog */}
        <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Task Details</DialogTitle>
          <DialogContent>
            {selectedTask && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Task ID:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{selectedTask.taskId}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Script:</Typography>
                  <Typography variant="body2">{selectedTask.scriptName}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Status:</Typography>
                  {getStatusChip(selectedTask.status)}
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Started By:</Typography>
                  <Typography variant="body2">{selectedTask.startedBy || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Duration:</Typography>
                  <Typography variant="body2">{selectedTask.duration}</Typography>
                </Box>
                {selectedTask.progressTotal > 0 && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Progress:</Typography>
                    <Typography variant="body2">
                      {selectedTask.progressCurrent}/{selectedTask.progressTotal} ({selectedTask.progressPercent}%)
                    </Typography>
                  </Box>
                )}
                {selectedTask.progressMessage && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Message:</Typography>
                    <Typography variant="body2">{selectedTask.progressMessage}</Typography>
                  </Box>
                )}
                {selectedTask.errorMessage && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">Error:</Typography>
                    <Paper sx={{ p: 1, bgcolor: 'error.dark', color: 'error.contrastText', mt: 0.5 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                        {selectedTask.errorMessage}
                      </Typography>
                    </Paper>
                  </Box>
                )}
                {selectedTask.isCancelled && selectedTask.cancellationReason && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">Cancellation Reason:</Typography>
                    <Typography variant="body2">{selectedTask.cancellationReason}</Typography>
                  </Box>
                )}
                {selectedTask.resultSummary && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">Result:</Typography>
                    <Typography variant="body2">{selectedTask.resultSummary}</Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 0.5,
          borderBottom: isCollapsed ? 0 : 1,
          borderColor: 'divider',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={onToggleCollapse}
      >
        <IconButton size="small" sx={{ p: 0.5 }}>
          {isCollapsed ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
        </IconButton>

        <Badge badgeContent={runningCount} color="info" max={99}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Task Monitor
          </Typography>
        </Badge>

        <Box sx={{ flex: 1 }} />

        {!isCollapsed && (
          <>
            <Tooltip title={autoRefresh ? 'Pause auto-refresh' : 'Resume auto-refresh'}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setAutoRefresh(!autoRefresh);
                }}
                color={autoRefresh ? 'primary' : 'default'}
              >
                {autoRefresh ? <PauseIcon fontSize="small" /> : <ResumeIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh now">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  fetchTasks();
                }}
                disabled={loading}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}

        {stats && !isCollapsed && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {stats.running > 0 && (
              <Chip label={`${stats.running} running`} size="small" color="info" sx={{ height: 20, fontSize: 10 }} />
            )}
            {stats.failed > 0 && (
              <Chip label={`${stats.failed} failed`} size="small" color="error" sx={{ height: 20, fontSize: 10 }} />
            )}
          </Box>
        )}
      </Box>

      {/* Content */}
      <Collapse in={!isCollapsed}>
        <Box sx={{ p: 1, maxHeight: 250, overflow: 'auto' }}>
          {tasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No tasks
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'background.default' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Script</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Progress</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 0.5 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.map((task) => (
                    <TableRow key={task.taskId} hover>
                      <TableCell sx={{ py: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 12 }}>
                          {task.scriptName}
                        </Typography>
                        {task.progressMessage && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {task.progressMessage}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        {getStatusChip(task.status)}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, minWidth: 120 }}>
                        {task.status === 'RUNNING' && task.progressTotal > 0 ? (
                          <Box>
                            <LinearProgress
                              variant="determinate"
                              value={task.progressPercent}
                              sx={{ height: 6, borderRadius: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {task.progressCurrent}/{task.progressTotal} ({task.progressPercent}%)
                            </Typography>
                          </Box>
                        ) : task.status === 'RUNNING' ? (
                          <LinearProgress sx={{ height: 6, borderRadius: 1 }} />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: 12 }}>
                          {task.duration}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }} align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewDetails(task)}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(task.status === 'RUNNING' || task.status === 'PENDING') && (
                            <Tooltip title="Cancel">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleCancelClick(task)}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Collapse>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Task</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to cancel task "{selectedTask?.scriptName}"?
          </Typography>
          <TextField
            label="Reason (optional)"
            fullWidth
            size="small"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="e.g., Taking too long"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Keep Running</Button>
          <Button onClick={handleCancelConfirm} color="error" variant="contained">
            Cancel Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Task Details</DialogTitle>
        <DialogContent>
          {selectedTask && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Task ID:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11 }}>{selectedTask.taskId}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Script:</Typography>
                <Typography variant="body2">{selectedTask.scriptName}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Status:</Typography>
                {getStatusChip(selectedTask.status)}
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Started By:</Typography>
                <Typography variant="body2">{selectedTask.startedBy || '-'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Duration:</Typography>
                <Typography variant="body2">{selectedTask.duration}</Typography>
              </Box>
              {selectedTask.progressTotal > 0 && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Progress:</Typography>
                  <Typography variant="body2">
                    {selectedTask.progressCurrent}/{selectedTask.progressTotal} ({selectedTask.progressPercent}%)
                  </Typography>
                </Box>
              )}
              {selectedTask.progressMessage && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Message:</Typography>
                  <Typography variant="body2">{selectedTask.progressMessage}</Typography>
                </Box>
              )}
              {selectedTask.errorMessage && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Error:</Typography>
                  <Paper sx={{ p: 1, bgcolor: 'error.dark', color: 'error.contrastText', mt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                      {selectedTask.errorMessage}
                    </Typography>
                  </Paper>
                </Box>
              )}
              {selectedTask.isCancelled && selectedTask.cancellationReason && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Cancellation Reason:</Typography>
                  <Typography variant="body2">{selectedTask.cancellationReason}</Typography>
                </Box>
              )}
              {selectedTask.resultSummary && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">Result:</Typography>
                  <Typography variant="body2">{selectedTask.resultSummary}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FlexTaskMonitor;
